import { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import { getStockCards } from "../../api/stockCardsAPI";
import { postSlots, putSlots, deleteSlots } from "../../api/slotsAPI";
import { createAndDownloadExcel, generateExcelFilename } from "../../utils/excelExportUtils";

const DEFAULT_COLS = 6;
const MIN_COLS = 1;
const MAX_COLS = 20;
const MAX_ROWS = 30;

// Mevcut slotlardan otomat layout'unu tahmin et
function detectLayout(existingSlots) {
  if (!existingSlots || existingSlots.length === 0) {
    return {
      startRow: 1,
      rowConfigs: [
        { id: 1, cols: DEFAULT_COLS },
        { id: 2, cols: DEFAULT_COLS },
        { id: 3, cols: DEFAULT_COLS },
        { id: 4, cols: DEFAULT_COLS },
      ],
    };
  }

  // Slot no'dan sıra ve sütun çıkar (örn: 23 → sıra=2, sütun=3)
  const rowMap = {};
  existingSlots.forEach((slot) => {
    const rowNum = Math.floor(slot.slotNo / 10);
    const colNum = slot.slotNo % 10;
    if (!rowMap[rowNum]) rowMap[rowNum] = 0;
    if (colNum > rowMap[rowNum]) rowMap[rowNum] = colNum;
  });

  const sortedRowNums = Object.keys(rowMap)
    .map(Number)
    .sort((a, b) => a - b);

  if (sortedRowNums.length === 0) {
    return {
      startRow: 1,
      rowConfigs: [{ id: 1, cols: DEFAULT_COLS }],
    };
  }

  const startRow = sortedRowNums[0];
  const endRow = sortedRowNums[sortedRowNums.length - 1];

  // Aradaki boş sıraları da dahil et (varsayılan sütun sayısıyla)
  const rowConfigs = [];
  for (let r = startRow; r <= endRow; r++) {
    rowConfigs.push({
      id: r,
      cols: rowMap[r] || DEFAULT_COLS,
    });
  }

  return { startRow, rowConfigs };
}

export default function SlotDragDropDialog({
  open,
  onClose,
  selectedConfig,
  existingSlots,
  onSaved,
}) {
  const { t } = useTranslation();

  const [stockCards, setStockCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Layout config
  const [startRow, setStartRow] = useState(1);
  const [rowConfigs, setRowConfigs] = useState([{ id: 1, cols: DEFAULT_COLS }]);
  // nextRowId — unique key için
  const [nextRowId, setNextRowId] = useState(100);

  // localSlots: { [slotNo]: { stockId, stockName, id, itemCount, priceOverride, slotPrice, isNew, isDeleted, isModified } }
  const [localSlots, setLocalSlots] = useState({});
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [draggedStock, setDraggedStock] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // JSON yükleme dialog state
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonInputText, setJsonInputText] = useState("");
  const [jsonParseError, setJsonParseError] = useState("");

  // Dialog açıldığında: stokları yükle ve mevcut slotlardan layout'u tespit et
  useEffect(() => {
    if (!open) return;

    const loadStocks = async () => {
      try {
        setLoading(true);
        const stocks = await getStockCards();
        setStockCards(stocks);
      } catch (error) {
        console.error("Error loading stocks:", error);
      } finally {
        setLoading(false);
      }
    };

    loadStocks();

    // Layout tespiti
    const { startRow: detectedStart, rowConfigs: detectedRows } = detectLayout(existingSlots);
    setStartRow(detectedStart);
    setRowConfigs(detectedRows);
    setNextRowId(detectedRows.length + 100);

    // Lokal slot haritası
    const initial = {};
    (existingSlots || []).forEach((slot) => {
      initial[slot.slotNo] = {
        stockId: slot.stockId,
        stockName: slot.stockName,
        id: slot.id,
        itemCount: slot.itemCount || 10,
        priceOverride: slot.priceOverride || 0,
        slotPrice: slot.slotPrice || 0,
        isNew: false,
        isDeleted: false,
        isModified: false,
      };
    });

    setLocalSlots(initial);
    setSearchQuery("");
    setDraggedStock(null);
    setDragOverSlot(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Slot numarası üretimi: her sıra için ayrı sütun sayısı
  const generatedSlotNums = useMemo(() => {
    const result = [];
    rowConfigs.forEach((row, rowIndex) => {
      const rowNum = startRow + rowIndex;
      for (let col = 1; col <= row.cols; col++) {
        result.push({ slotNo: rowNum * 10 + col, rowIndex });
      }
    });
    return result;
  }, [rowConfigs, startRow]);

  // Sıra bazlı gruplandırma (rendering için)
  const slotsByRow = useMemo(() => {
    return rowConfigs.map((row, rowIndex) => {
      const rowNum = startRow + rowIndex;
      const slots = [];
      for (let col = 1; col <= row.cols; col++) {
        slots.push(rowNum * 10 + col);
      }
      return { rowNum, rowId: row.id, slots };
    });
  }, [rowConfigs, startRow]);

  // ===== Layout handlers =====

  const handleAddRow = () => {
    if (rowConfigs.length >= MAX_ROWS) return;
    setRowConfigs((prev) => [...prev, { id: nextRowId, cols: DEFAULT_COLS }]);
    setNextRowId((n) => n + 1);
  };

  const handleRemoveRow = (rowId) => {
    if (rowConfigs.length <= 1) return;
    setRowConfigs((prev) => prev.filter((r) => r.id !== rowId));
  };

  const handleUpdateCols = (rowId, delta) => {
    setRowConfigs((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, cols: Math.min(MAX_COLS, Math.max(MIN_COLS, r.cols + delta)) }
          : r
      )
    );
  };

  const handleStartRowChange = (value) => {
    const num = parseInt(value);
    if (!isNaN(num) && num >= 0 && num <= 99) setStartRow(num);
  };

  // ===== Drag & Drop handlers =====

  const filteredStocks = stockCards.filter((s) =>
    s.stockName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDragStart = (e, stock) => {
    setDraggedStock(stock);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDragEnd = () => {
    setDraggedStock(null);
    setDragOverSlot(null);
  };

  const handleDragOver = (e, slotNo) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOverSlot(slotNo);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverSlot(null);
    }
  };

  const handleDrop = (e, slotNo) => {
    e.preventDefault();
    setDragOverSlot(null);
    if (!draggedStock) return;

    const existing = localSlots[slotNo];
    const stockChanged =
      existing && !existing.isDeleted && existing.stockId !== draggedStock.idstockTable;

    setLocalSlots((prev) => ({
      ...prev,
      [slotNo]: {
        stockId: draggedStock.idstockTable,
        stockName: draggedStock.stockName,
        id: existing?.id || null,
        itemCount: existing?.itemCount || 10,
        priceOverride: existing?.priceOverride || 0,
        slotPrice: existing?.slotPrice || 0,
        isNew: !existing?.id,
        isDeleted: false,
        isModified: existing?.id ? stockChanged : false,
      },
    }));

    setDraggedStock(null);
  };

  const handleClearSlot = (slotNo) => {
    const existing = localSlots[slotNo];
    if (!existing || existing.isDeleted) return;

    if (existing.id) {
      setLocalSlots((prev) => ({
        ...prev,
        [slotNo]: { ...prev[slotNo], isDeleted: true },
      }));
    } else {
      setLocalSlots((prev) => {
        const updated = { ...prev };
        delete updated[slotNo];
        return updated;
      });
    }
  };

  // ===== JSON Load =====

  const handleJsonApply = () => {
    setJsonParseError("");
    let parsed;
    try {
      parsed = JSON.parse(jsonInputText);
    } catch {
      setJsonParseError("Geçersiz JSON formatı.");
      return;
    }

    if (!Array.isArray(parsed) || parsed.length === 0) {
      setJsonParseError("JSON bir dizi (array) olmalı ve en az 1 eleman içermeli.");
      return;
    }

    const invalid = parsed.find(
      (item) => item.SNO === undefined || item.PCODE === undefined || item.ICOUNT === undefined
    );
    if (invalid) {
      setJsonParseError("Her eleman SNO, PCODE ve ICOUNT alanlarını içermeli.");
      return;
    }

    // PCODE → stok kartı eşleştir (Türkçe büyük/küçük harf duyarsız, iki aşamalı)
    const normalize = (str) => String(str).trim().toLocaleLowerCase("tr-TR");
    const fuzzy = (str) => normalize(str).replace(/[\s.,\-_()]+/g, "");

    const stockByName = {};
    const stockByFuzzy = {};
    stockCards.forEach((s) => {
      stockByName[normalize(s.stockName)] = s;
      stockByFuzzy[fuzzy(s.stockName)] = s;
    });

    const findStock = (pcode) =>
      stockByName[normalize(pcode)] || stockByFuzzy[fuzzy(pcode)] || null;

    const unmatched = [];
    const newSlots = { ...localSlots };

    parsed.forEach((item) => {
      const slotNo = parseInt(item.SNO);
      const pcode = String(item.PCODE).trim();
      const icount = parseInt(item.ICOUNT) || 10;
      const stock = findStock(pcode);

      if (!stock) {
        if (!unmatched.includes(pcode)) unmatched.push(pcode);
        return;
      }

      const existing = newSlots[slotNo];
      const stockChanged = existing && !existing.isDeleted && existing.stockId !== stock.idstockTable;

      newSlots[slotNo] = {
        stockId: stock.idstockTable,
        stockName: stock.stockName,
        id: existing?.id || null,
        itemCount: icount,
        priceOverride: existing?.priceOverride || 0,
        slotPrice: existing?.slotPrice || 0,
        isNew: !existing?.id,
        isDeleted: false,
        isModified: existing?.id ? stockChanged : false,
      };
    });

    setLocalSlots(newSlots);

    // Layout'u JSON'daki SNO'lardan güncelle
    const rowMap = {};
    parsed.forEach((item) => {
      const sno = parseInt(item.SNO);
      const rowNum = Math.floor(sno / 10);
      const colNum = sno % 10;
      if (!rowMap[rowNum]) rowMap[rowNum] = 0;
      if (colNum > rowMap[rowNum]) rowMap[rowNum] = colNum;
    });

    const sortedRowNums = Object.keys(rowMap).map(Number).sort((a, b) => a - b);
    if (sortedRowNums.length > 0) {
      const newStartRow = sortedRowNums[0];
      const endRow = sortedRowNums[sortedRowNums.length - 1];
      const newRowConfigs = [];
      for (let r = newStartRow; r <= endRow; r++) {
        newRowConfigs.push({ id: r, cols: rowMap[r] || DEFAULT_COLS });
      }
      setStartRow(newStartRow);
      setRowConfigs(newRowConfigs);
      setNextRowId(endRow + 100);
    }

    setJsonDialogOpen(false);
    setJsonInputText("");

    if (unmatched.length > 0) {
      setSnackbar({
        open: true,
        message: `JSON yüklendi. Eşleşmeyen ürünler (${unmatched.length}): ${unmatched.slice(0, 3).join(", ")}${unmatched.length > 3 ? " ..." : ""}`,
        severity: "warning",
      });
    } else {
      setSnackbar({ open: true, message: "JSON başarıyla yüklendi.", severity: "success" });
    }
  };

  // ===== Save =====

  const handleSave = async () => {
    if (!selectedConfig) return;

    try {
      setSaving(true);
      let savedCount = 0;
      let deletedCount = 0;

      for (const [slotNoStr, slotData] of Object.entries(localSlots)) {
        const slotNo = parseInt(slotNoStr);

        if (slotData.isDeleted && slotData.id) {
          await deleteSlots(slotData.id);
          deletedCount++;
        } else if (!slotData.isDeleted && slotData.isNew) {
          await postSlots({
            slotNo,
            itemCount: slotData.itemCount || 10,
            priceOverride: 0,
            slotPrice: 0,
            slotDefinitionTable_idslotDefinitionTable: selectedConfig.id,
            stockTable_idStockTable: slotData.stockId,
          });
          savedCount++;
        } else if (!slotData.isDeleted && slotData.isModified && slotData.id) {
          await putSlots({
            idslotTable: slotData.id,
            slotNo,
            itemCount: slotData.itemCount || 10,
            priceOverride: slotData.priceOverride ? 1 : 0,
            slotPrice: slotData.slotPrice || 0,
            slotDefinitionTable_idslotDefinitionTable: selectedConfig.id,
            stockTable_idStockTable: slotData.stockId,
          });
          savedCount++;
        }
      }

      setSnackbar({
        open: true,
        message:
          `${savedCount} slot kaydedildi` +
          (deletedCount > 0 ? `, ${deletedCount} slot silindi` : "") +
          ".",
        severity: "success",
      });

      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving slots:", error);
      setSnackbar({
        open: true,
        message: "Kaydetme hatası: " + error.message,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const pendingChanges = Object.values(localSlots).filter(
    (s) => s.isNew || s.isDeleted || s.isModified
  ).length;

  // ===== Render =====

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <DragIndicatorIcon color="primary" />
          <span>{selectedConfig?.name} — Hızlı Slot Düzenleme</span>
          {pendingChanges > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 1.25,
                py: 0.25,
                bgcolor: "warning.main",
                color: "warning.contrastText",
                borderRadius: 10,
                fontSize: "0.72rem",
                fontWeight: "bold",
              }}
            >
              {pendingChanges} değişiklik
            </Box>
          )}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* ===== Sol: Sürüklenebilir ürün listesi ===== */}
            <Box
              sx={{
                width: 240,
                flexShrink: 0,
                borderRight: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                p: 1.5,
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Ürün Listesi
              </Typography>
              <TextField
                size="small"
                placeholder="Ürün ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")} edge="end">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {filteredStocks.map((stock) => (
                    <Paper
                      key={stock.idstockTable}
                      draggable
                      onDragStart={(e) => handleDragStart(e, stock)}
                      onDragEnd={handleDragEnd}
                      elevation={1}
                      sx={{
                        px: 1,
                        py: 0.75,
                        cursor: "grab",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        userSelect: "none",
                        opacity: draggedStock?.idstockTable === stock.idstockTable ? 0.35 : 1,
                        transition: "opacity 0.15s",
                        "&:hover": { bgcolor: "action.hover" },
                        "&:active": { cursor: "grabbing" },
                      }}
                    >
                      <DragIndicatorIcon
                        fontSize="small"
                        sx={{ color: "text.disabled", flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 500, lineHeight: 1.3 }}
                        >
                          {stock.stockName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(stock.stockPrice / 100).toFixed(2)} ₺
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                  {filteredStocks.length === 0 && !loading && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", mt: 3 }}
                    >
                      Ürün bulunamadı
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* ===== Orta: Layout konfigüratörü ===== */}
            <Box
              sx={{
                width: 220,
                flexShrink: 0,
                borderRight: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                p: 1.5,
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Otomat Yapısı
              </Typography>

              {/* Başlangıç sırası */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Başlangıç sırası
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleStartRowChange(startRow - 1)}
                  disabled={startRow <= 0}
                >
                  <RemoveIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
                <Typography variant="caption" sx={{ minWidth: 20, textAlign: "center", fontWeight: 600 }}>
                  {startRow}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleStartRowChange(startRow + 1)}
                  disabled={startRow >= 99}
                >
                  <AddIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Box>

              <Divider />

              {/* Sıra listesi */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {rowConfigs.map((row, rowIndex) => {
                  const rowNum = startRow + rowIndex;
                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        py: 0.25,
                      }}
                    >
                      {/* Sıra etiketi */}
                      <Typography
                        variant="caption"
                        sx={{
                          minWidth: 42,
                          color: "text.secondary",
                          fontSize: "0.72rem",
                        }}
                      >
                        Sıra {rowNum}
                      </Typography>

                      {/* Sütun sayısı −/sayı/+ */}
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateCols(row.id, -1)}
                        disabled={row.cols <= MIN_COLS}
                        sx={{ p: 0.25 }}
                      >
                        <RemoveIcon sx={{ fontSize: "0.85rem" }} />
                      </IconButton>
                      <Typography variant="caption" sx={{ minWidth: 20, textAlign: "center", fontWeight: 600 }}>
                        {row.cols}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateCols(row.id, +1)}
                        disabled={row.cols >= MAX_COLS}
                        sx={{ p: 0.25 }}
                      >
                        <AddIcon sx={{ fontSize: "0.85rem" }} />
                      </IconButton>

                      {/* Sırayı sil */}
                      <Tooltip title="Sırayı kaldır">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRow(row.id)}
                            disabled={rowConfigs.length <= 1}
                            sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "error.main" } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: "0.9rem" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>

              <Divider />

              {/* Sıra ekle butonu */}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                disabled={rowConfigs.length >= MAX_ROWS}
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              >
                Sıra Ekle
              </Button>

              {/* Özet */}
              <Box sx={{ pt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {rowConfigs.length} sıra · {generatedSlotNums.length} slot
                </Typography>
              </Box>
            </Box>

            {/* ===== Sağ: Slot haritası (drag & drop) ===== */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                p: 1.5,
                overflow: "hidden",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Slot Haritası
                </Typography>
                <Button
                  size="small"
                  startIcon={<FileUploadIcon />}
                  onClick={() => { setJsonInputText(""); setJsonParseError(""); setJsonDialogOpen(true); }}
                  variant="outlined"
                  sx={{ fontSize: "0.72rem", py: 0.25 }}
                >
                  JSON Yükle
                </Button>
                <Box sx={{ flex: 1 }} />
                {/* Renk açıklamaları */}
                {[
                  { color: "success.main", label: "Yeni" },
                  { color: "warning.main", label: "Değişti" },
                  { color: "primary.light", label: "Mevcut" },
                  { color: "divider", label: "Boş" },
                ].map(({ color, label }) => (
                  <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        border: "2px solid",
                        borderColor: color,
                        borderRadius: 0.5,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography variant="caption" color="text.disabled" sx={{ mb: 1.5 }}>
                Soldan ürün sürükle → slota bırak
              </Typography>

              {/* Sıra sıra slot grid */}
              <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
                {slotsByRow.map(({ rowNum, rowId, slots }) => (
                  <Box
                    key={rowId}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}
                  >
                    {/* Sıra etiketi */}
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 32,
                        color: "text.disabled",
                        fontSize: "0.65rem",
                        textAlign: "right",
                        pr: 0.5,
                        userSelect: "none",
                      }}
                    >
                      {rowNum}→
                    </Typography>

                    {/* Slot kutuları */}
                    {slots.map((slotNo) => {
                      const slotData = localSlots[slotNo];
                      const isOccupied = slotData && !slotData.isDeleted;
                      const isDragOver = dragOverSlot === slotNo;

                      let borderColor = "divider";
                      if (isDragOver) borderColor = "primary.main";
                      else if (isOccupied) {
                        if (slotData.isNew) borderColor = "success.main";
                        else if (slotData.isModified) borderColor = "warning.main";
                        else borderColor = "primary.light";
                      }

                      return (
                        <Paper
                          key={slotNo}
                          onDragOver={(e) => handleDragOver(e, slotNo)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, slotNo)}
                          elevation={isDragOver ? 5 : 1}
                          sx={{
                            width: 90,
                            minHeight: 70,
                            flexShrink: 0,
                            p: 0.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            border: "2px solid",
                            borderColor,
                            bgcolor: isDragOver
                              ? "action.focus"
                              : isOccupied
                              ? "action.selected"
                              : "background.paper",
                            transition: "border-color 0.1s, box-shadow 0.1s",
                            cursor: "default",
                          }}
                        >
                          {/* Slot numarası */}
                          <Typography
                            variant="caption"
                            sx={{
                              position: "absolute",
                              top: 2,
                              left: 4,
                              fontWeight: "bold",
                              color: "text.disabled",
                              fontSize: "0.58rem",
                              lineHeight: 1,
                              userSelect: "none",
                            }}
                          >
                            {slotNo}
                          </Typography>

                          {isOccupied ? (
                            <>
                              <Tooltip title="Slotu temizle">
                                <IconButton
                                  size="small"
                                  onClick={() => handleClearSlot(slotNo)}
                                  sx={{
                                    position: "absolute",
                                    top: 1,
                                    right: 1,
                                    p: 0.2,
                                    "& svg": { fontSize: "0.65rem" },
                                    color: "text.secondary",
                                    "&:hover": { color: "error.main" },
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </Tooltip>
                              <Typography
                                variant="caption"
                                align="center"
                                fontWeight={500}
                                sx={{
                                  mt: 1.5,
                                  px: 0.25,
                                  lineHeight: 1.2,
                                  wordBreak: "break-word",
                                  textAlign: "center",
                                  fontSize: "0.65rem",
                                }}
                              >
                                {slotData.stockName}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDragOver ? "primary.main" : "text.disabled",
                                mt: 1.5,
                                fontStyle: "italic",
                                fontWeight: isDragOver ? 600 : 400,
                                fontSize: "0.65rem",
                                userSelect: "none",
                              }}
                            >
                              {isDragOver ? "Bırak" : "Boş"}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {pendingChanges > 0
              ? `${pendingChanges} değişiklik kaydedilmeyi bekliyor`
              : "Değişiklik yok — mevcut durumu gösteriyor"}
          </Typography>
          <Button onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving || pendingChanges === 0}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== JSON Yükleme Alt Dialogu ===== */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "80vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FileUploadIcon color="primary" />
          JSON ile Slot Yükle
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.5, overflow: "hidden" }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Aşağıdaki formatta bir JSON yapıştırın. <b>PCODE</b> stok adıyla eşleştirilir.
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{
                display: "block",
                bgcolor: "action.hover",
                p: 1,
                borderRadius: 1,
                mt: 0.75,
                fontFamily: "monospace",
                fontSize: "0.7rem",
                overflowX: "auto",
              }}
            >
              {`[{ "SNO": 11, "ICOUNT": 10, "PCODE": "Ürün Adı", "PRICE": 2000 }, ...]`}
            </Typography>
          </Box>
          <TextField
            multiline
            fullWidth
            placeholder="JSON buraya yapıştırın..."
            value={jsonInputText}
            onChange={(e) => { setJsonInputText(e.target.value); setJsonParseError(""); }}
            error={!!jsonParseError}
            helperText={jsonParseError}
            inputProps={{ style: { fontFamily: "monospace", fontSize: "0.78rem" } }}
            sx={{ flex: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" }, "& textarea": { height: "100% !important", overflowY: "auto !important" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              const columns = [
                { field: "stockName", headerName: "PCODE (Stok Adı)" },
                { field: "stockPrice", headerName: "Fiyat (Kuruş)" },
              ];
              createAndDownloadExcel(
                stockCards,
                columns,
                "Stok Listesi",
                generateExcelFilename("Stok_Listesi")
              );
            }}
            disabled={stockCards.length === 0}
            sx={{ mr: "auto" }}
          >
            Stok Listesini İndir
          </Button>
          <Button onClick={() => setJsonDialogOpen(false)}>İptal</Button>
          <Button
            onClick={handleJsonApply}
            variant="contained"
            disabled={!jsonInputText.trim()}
            startIcon={<FileUploadIcon />}
          >
            Yükle
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
